/**
 * İstasyon Koşusu ortak kuralları (istemci + sunucu). Tek puan: doğru cevap +100, altın +1.
 * Yanlış cevap: puan ≥ 50 ise −50; değilse puan değişmez ve 1 can gider. Engele çarpmak 1 can götürür.
 * Sunucu bölüm olaylarını ve cevap sırasını `replayStationRun` ile yeniden oynatır.
 */
export const STATION_RUN_RULES = {
  minQuestions: 5, maxQuestions: 20, minChoices: 2, maxChoices: 4,
  startingLives: 4, correctScore: 100, wrongPenalty: 50, coinScore: 1,
  segmentMeters: 100,
  /** Bir bölümde üretilebilecek en fazla altın ve olası en fazla çarpma (can sayısı kadar). */
  maxCoinsPerSegment: 40, maxHitsPerSegment: 4,
} as const;

export type StationRunSegment = { coins: number; hits: number };
export type StationRunAnswerOutcome = { scoreDelta: number; livesLost: 0 | 1 };

/** Yanlış cevabın etkisi o anki puana bağlıdır: yeterli puan varsa puan, yoksa can düşer. */
export function stationRunAnswer(score: number, correct: boolean): StationRunAnswerOutcome {
  if (correct) return { scoreDelta: STATION_RUN_RULES.correctScore, livesLost: 0 };
  if (score >= STATION_RUN_RULES.wrongPenalty) return { scoreDelta: -STATION_RUN_RULES.wrongPenalty, livesLost: 0 };
  return { scoreDelta: 0, livesLost: 1 };
}

export type StationRunReplayStep = { kind: 'segment'; index: number; score: number; lives: number } | { kind: 'answer'; index: number; score: number; lives: number; scoreDelta: number; livesLost: 0 | 1 };

/**
 * Bölüm i (altın + çarpma) → istasyon i sorusu sırasıyla oynatır; can 0 olunca durur.
 * `valid`: olay listeleri oyunun gerçek bitiş noktasıyla tam uyumlu mu (bitişten sonra olay yok, bitmeden eksik olay yok).
 */
export function replayStationRun(segments: readonly StationRunSegment[], answers: readonly boolean[], questionCount: number) {
  let score = 0, lives: number = STATION_RUN_RULES.startingLives, lost = false, valid = true;
  const steps: StationRunReplayStep[] = [];
  let segmentsUsed = 0, answersUsed = 0;
  for (let i = 0; i < questionCount && !lost; i += 1) {
    const segment = segments[i];
    if (!segment) { valid = false; break; }
    segmentsUsed += 1;
    if (segment.coins > STATION_RUN_RULES.maxCoinsPerSegment || segment.hits > lives) valid = false;
    score += segment.coins * STATION_RUN_RULES.coinScore;
    lives = Math.max(0, lives - segment.hits);
    steps.push({ kind: 'segment', index: i, score, lives });
    if (lives === 0) { lost = true; break; }
    if (answers.length <= i) { valid = false; break; }
    answersUsed += 1;
    const outcome = stationRunAnswer(score, answers[i]);
    score += outcome.scoreDelta; lives -= outcome.livesLost;
    steps.push({ kind: 'answer', index: i, score, lives, ...outcome });
    if (lives === 0) lost = true;
  }
  if (segments.length !== segmentsUsed || answers.length !== answersUsed) valid = false;
  return { valid, completed: valid && !lost, lost, score, lives, steps };
}
